import React, { useCallback, useMemo, useState } from 'react'
import { Calendar } from 'react-date-range'
import PropTypes from 'prop-types'
import { isAfter } from 'date-fns'
import classnames from 'classnames'

import SelectDropdown from '../SelectDropdown'

import { formatDateFullYear, MINIMAL_PAST_DATE } from '../../constants/dates'

import useStyles from './styles'

const DatePicker = ({
  className,
  value,
  placeholder,
  changeHandler,
  minDate,
  maxDate,
  isPastAllowed = false,
  disabled
}) => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)

  // If minDate not provided - set current date as minimal
  const minDateConverted = useMemo(() => (minDate ? new Date(minDate) : new Date()), [minDate])
  const valueDate = useMemo(() => (value ? new Date(value) : minDateConverted), [value, minDateConverted])
  const valueDateAfter = useMemo(() => isAfter(valueDate, minDateConverted), [valueDate, minDateConverted])
  // SelectDropdown component variables
  const selectValue = useMemo(() => (value ? formatDateFullYear(valueDate) : ''), [value, valueDate])
  // Calendar component variables
  const shownDate = useMemo(
    () => (valueDateAfter ? valueDate : minDateConverted),
    [valueDate, valueDateAfter, minDateConverted]
  )
  const minCalendarDate = useMemo(
    () => (isPastAllowed ? new Date(MINIMAL_PAST_DATE) : minDateConverted),
    [isPastAllowed, minDateConverted]
  )
  const maxCalendarDate = useMemo(() => maxDate && new Date(maxDate), [maxDate])
  const date = useMemo(() => (valueDateAfter ? valueDate : null), [valueDateAfter, valueDate])

  const handleDropdownVisibility = useCallback(
    show => {
      setIsOpen(show)
    },
    [setIsOpen]
  )

  const handleChange = useCallback(
    date => {
      setIsOpen(false)

      // handling outside changes, like setFieldValue in formik
      changeHandler && changeHandler(date)
    },
    [changeHandler]
  )

  return (
    <div className={classnames('field', className)}>
      <SelectDropdown
        className={classes.datePickerSelect}
        value={selectValue}
        placeholder={placeholder}
        isManuallyOpened={!disabled && isOpen}
        onVisibilityChange={handleDropdownVisibility}
        isDisabled={disabled}
      >
        <Calendar
          className={classes.calendar}
          // do not open date which is before minDate(avoid opening 2041 year)
          shownDate={shownDate}
          // if pastDate allowed - set minimal past date
          minDate={minCalendarDate}
          maxDate={maxCalendarDate}
          onChange={handleChange}
          // do not represent date which is before minDate
          date={date}
        />
      </SelectDropdown>
    </div>
  )
}

export default DatePicker

DatePicker.propTypes = {
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  placeholder: PropTypes.string,
  changeHandler: PropTypes.func,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
}
